import type { ReactElement, ReactNode } from "react";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";

export interface FormFieldWrapperRenderProps<FormValues = string> {
  field: FieldInputProps<FormValues>;
  meta: FieldMetaProps<FormValues>;
  showError: boolean;
}

export type FormFieldWrapperProps<FormValues = string> = {
  name: string;
  label: string;
  helperText?: ReactNode;
  id: string;
  isRequired?: boolean;
  children: (
    props: FormFieldWrapperRenderProps<FormValues> & {
      describedBy?: string;
    },
  ) => ReactNode;
};

const renderHelperText = (helperText: ReactNode, helperId: string) => {
  if (typeof helperText !== "string") {
    return (
      <div id={helperId} className="form-text mb-0">
        {helperText}
      </div>
    );
  }

  const paragraphs = helperText
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (paragraphs.length <= 1) {
    return (
      <div id={helperId} className="form-text mb-0">
        {paragraphs[0] ?? helperText}
      </div>
    );
  }

  return (
    <div id={helperId} className="form-text mb-0">
      {paragraphs.map((paragraph, index) => (
        <p
          key={index}
          className={index < paragraphs.length - 1 ? "mb-2" : "mb-0"}
        >
          {paragraph}
        </p>
      ))}
    </div>
  );
};

export function FormFieldWrapper<FormValues = string>(
  props: FormFieldWrapperProps<FormValues>,
): ReactElement {
  const { name, label, helperText, id, isRequired = false, children } = props;
  const { submitCount } = useFormikContext();
  const helperId = helperText ? `${id}-helper` : undefined;
  const errorId = `${id}-error`;

  return (
    <Field name={name}>
      {({ field, meta }: FormFieldWrapperRenderProps<FormValues>) => {
        const showError = Boolean(
          meta.error && (meta.touched || submitCount > 0),
        );
        const describedBy =
          [helperId, showError ? errorId : undefined]
            .filter(Boolean)
            .join(" ") || undefined;

        return (
          <div className="d-flex flex-column gap-1">
            <label className="form-label text-wrap" htmlFor={id}>
              {label}
              {isRequired ? (
                <span className="text-danger" aria-hidden="true">
                  {" *"}
                </span>
              ) : null}
            </label>
            {children({ field, meta, showError, describedBy })}
            {helperText && helperId
              ? renderHelperText(helperText, helperId)
              : null}
            {showError ? (
              <p
                id={errorId}
                role="alert"
                className="small text-danger mb-0 text-wrap"
              >
                {meta.error}
              </p>
            ) : null}
          </div>
        );
      }}
    </Field>
  );
}
